import { createAsyncThunk } from "@reduxjs/toolkit"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import type {
    IOneVerifyEraUploadCompleteRequestData,
    IOneVerifyEraUploadPresignRequestData,
    IOneVerifyIntegrationSupportRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IOneVerifyEraUploadCompleteResponse } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { IntegrationsService } from "../../services/integrations.service"
import type { IIntegrationsEraUploadThunkPayload } from "../../types/integrations.types"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

export const INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY =
    "integrationsSupportRequestLoading"

export const INTEGRATIONS_ERA_UPLOAD_LOADING_KEY =
    "integrationsEraUploadLoading"

export const integrationsSubmitSupportRequest = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyIntegrationSupportRequestData>
>("integrationsSubmitSupportRequestPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading(INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY))

    try {
        const result =
            await IntegrationsService.submitIntegrationSupportRequest(
                action.practice,
                action.payload,
            )

        if (result) {
            ToastrHelper.success(
                "Thanks — we received your request and will follow up.",
            )

            action.onSuccess?.(result)
        } else {
            ToastrHelper.error(
                "Sorry, we couldn't submit your request. Please try again later.",
            )

            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading(INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY))

        action.onFinally?.()
    }
})

export const integrationsUploadEraFile = createAsyncThunk<
    IOneVerifyEraUploadCompleteResponse | undefined,
    IThunkActionWithPracticeData<IIntegrationsEraUploadThunkPayload>
>("integrationsUploadEraFilePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading(INTEGRATIONS_ERA_UPLOAD_LOADING_KEY))

    try {
        const presignPayload: IOneVerifyEraUploadPresignRequestData = {
            fileName: action.payload.file.name,
            fileSizeBytes: action.payload.file.size,
            fileFormat: action.payload.fileFormat,
        }

        const presign = await IntegrationsService.createEraUploadPresignedUrl(
            action.practice,
            presignPayload,
        )

        if (!presign) {
            ToastrHelper.error(
                "Sorry, we couldn't start the upload. Please try again later.",
            )

            action.onError?.(undefined)

            return undefined
        }

        const uploaded = await IntegrationsService.uploadFileToPresignedUrl(
            presign.uploadUrl,
            action.payload.file,
            presign.contentType,
        )

        if (!uploaded) {
            ToastrHelper.error(
                "Sorry, the file upload to storage failed. Please try again.",
            )

            action.onError?.(undefined)

            return undefined
        }

        const completePayload: IOneVerifyEraUploadCompleteRequestData = {
            s3Key: presign.s3Key,
            fileSizeBytes: action.payload.file.size,
            fileFormat: action.payload.fileFormat,
        }

        const complete = await IntegrationsService.completeEraUpload(
            action.practice,
            completePayload,
        )

        if (complete) {
            const successMessage =
                action.payload.fileFormat ===
                OneVerifyEraUploadFileFormat.x12
                    ? "Upload received. We will process your X12 835 file shortly."
                    : `Upload received (${action.payload.rowCount.toLocaleString()} rows). We will process your ERA file shortly.`

            ToastrHelper.success(successMessage)

            action.onSuccess?.(complete)

            return complete
        }

        ToastrHelper.error(
            "The file was uploaded but we could not confirm it. Contact support if this persists.",
        )

        action.onError?.(undefined)

        return undefined
    } catch (e) {
        action.onError?.(undefined)

        return undefined
    } finally {
        dispatch(runtimeStopLoading(INTEGRATIONS_ERA_UPLOAD_LOADING_KEY))

        action.onFinally?.()
    }
})
